import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getRestaurantTemplate } from '@onepos/shared';
import { Alert, Badge, Button, Card, EmptyState, Icon, Spinner, useAsync } from '@onepos/ui';
import { api } from '../lib/api';
import { STATUS, daysLeft, fmtAgo, fmtDaysLeft, fmtPhone } from '../lib/format';
import type { TenantStatus, TenantsPage } from '../lib/types';

const PAGE = 50;
const FILTERS: { value: '' | TenantStatus; label: string }[] = [
  { value: '', label: 'Hammasi' },
  { value: 'trial', label: 'Sinov' },
  { value: 'active', label: 'Faol' },
  { value: 'grace', label: 'Muhlat' },
  { value: 'suspended', label: 'To‘xtatilgan' },
  { value: 'deleted', label: 'O‘chirilgan' },
];

export function RestaurantsPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const q = params.get('q') ?? '';
  const status = (params.get('holat') ?? '') as '' | TenantStatus;
  const page = Math.max(0, Number(params.get('sahifa') ?? 0) || 0);
  const [text, setText] = useState(q);

  // Qidiruv: yozish to'xtagach 300 ms dan keyin
  useEffect(() => {
    const t = setTimeout(() => {
      if (text !== q) {
        const next = new URLSearchParams(params);
        if (text) next.set('q', text);
        else next.delete('q');
        next.delete('sahifa');
        setParams(next, { replace: true });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [text, q, params, setParams]);

  const { data, error, loading } = useAsync(
    () =>
      api.rpc<TenantsPage>('platform_tenants', {
        p_search: q || null,
        p_status: status || null,
        p_limit: PAGE,
        p_offset: page * PAGE,
      }),
    [q, status, page],
  );

  const setFilter = (v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set('holat', v);
    else next.delete('holat');
    next.delete('sahifa');
    setParams(next);
  };
  const setPage = (p: number) => {
    const next = new URLSearchParams(params);
    if (p) next.set('sahifa', String(p));
    else next.delete('sahifa');
    setParams(next);
  };

  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE)) : 1;

  return (
    <>
      <header className="cn-page-head">
        <div>
          <h1>Restoranlar</h1>
          <p>{data ? `${data.total} ta restoran` : 'Platformadagi barcha restoranlar'}</p>
        </div>
        <Link to="/restoranlar/yangi" className="op-btn op-btn--primary">
          <Icon name="plus" /> Yangi restoran
        </Link>
      </header>

      <div className="cn-toolbar">
        <label className="cn-search">
          <Icon name="search" />
          <span className="op-visually-hidden">Qidirish</span>
          <input
            type="search"
            placeholder="Nom, shahar, ega yoki telefon"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </label>
        <div className="cn-chips" role="group" aria-label="Holat bo‘yicha">
          {FILTERS.map((f) => (
            <button
              key={f.value || 'all'}
              type="button"
              className={`cn-chip${status === f.value ? ' cn-chip--on' : ''}`}
              aria-pressed={status === f.value}
              onClick={() => setFilter(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {error && <Alert tone="danger">{error.message}</Alert>}

      <Card className="cn-table-card">
        {loading && !data ? (
          <div style={{ padding: 24 }}>
            <Spinner />
          </div>
        ) : data && data.rows.length === 0 ? (
          <EmptyState title={q || status ? 'Hech narsa topilmadi' : 'Hali restoran yo‘q'}>
            {q || status ? 'Qidiruv yoki filtrni o‘zgartirib ko‘ring.' : 'Birinchi restoranni “Yangi restoran” tugmasi bilan oching.'}
          </EmptyState>
        ) : data ? (
          <table className="cn-table">
            <thead>
              <tr>
                <th>Restoran</th>
                <th>Holat</th>
                <th>Tarif</th>
                <th>Ega</th>
                <th className="cn-num">Qurilma</th>
                <th className="cn-num">Xodim</th>
                <th>Faollik</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => {
                const d = r.status === 'trial' ? daysLeft(r.trial_ends_at) : null;
                return (
                  <tr key={r.id} onClick={() => navigate(`/restoranlar/${r.id}`)} className="cn-table__row">
                    <td data-label="Restoran">
                      <Link to={`/restoranlar/${r.id}`} className="cn-table__name" onClick={(e) => e.stopPropagation()}>
                        {r.name}
                      </Link>
                      <span className="cn-table__sub">
                        {r.city ?? '—'} · {getRestaurantTemplate(r.template_key)?.label ?? r.template_key}
                      </span>
                    </td>
                    <td data-label="Holat">
                      <Badge tone={STATUS[r.status].tone}>{STATUS[r.status].label}</Badge>
                      {d !== null && (
                        <span className={`cn-table__sub${d <= 3 ? ' cn-warn' : ''}`}>{fmtDaysLeft(r.trial_ends_at)}</span>
                      )}
                    </td>
                    <td data-label="Tarif">{r.plan_label}</td>
                    <td data-label="Ega">
                      {r.owner_name ?? '—'}
                      <span className="cn-table__sub op-num">{fmtPhone(r.owner_phone)}</span>
                    </td>
                    <td data-label="Qurilma" className="cn-num op-num">
                      {r.devices}
                    </td>
                    <td data-label="Xodim" className="cn-num op-num">
                      {r.staff}
                    </td>
                    <td data-label="Faollik">{r.devices ? fmtAgo(r.last_seen_at) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : null}
      </Card>

      {data && pages > 1 && (
        <div className="cn-row" style={{ justifyContent: 'center' }}>
          <Button size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
            <Icon name="back" /> Oldingi
          </Button>
          <span className="cn-muted op-num">
            {page + 1} / {pages}
          </span>
          <Button size="sm" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}>
            Keyingi <Icon name="chevron" />
          </Button>
        </div>
      )}
    </>
  );
}
